import { stringify } from 'querystring'

import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import Head from 'next/head'
import dayjs from 'dayjs'
import { Icon } from '@material-ui/core'

import { Text } from '@wartek-id/text'

import ModuleDetailsPlaceholder from '../partials/Module/ModuleDetailsPlaceholder'
import PostTestCard from '../partials/Module/PostTestCard'
import { GATrackEvent } from 'utils/googleAnalytics'
import ModuleDrawer from '../partials/Module/ModuleDrawer'
import CheckmarkIcon from '../components/CheckmarkIcon'
import MaterialList from '../components/MaterialList'
import ModulePageHeading from '../components/ModulePageHeading'

import type {
  MaterialDataResponse,
  ResponseModulData,
  PostTestState,
} from 'types/microlearning'
import type { CommonListResponse, CommonResponse } from 'types/api'

type ModuleDetailsMobileProps = {
  modulePreview: CommonResponse<ResponseModulData>
  isLoading: boolean
  isSuccess: boolean
  materialData: CommonListResponse<MaterialDataResponse, any>
  toggleShowDetail: () => void
  toggleMateriExpand: (string) => void
  isDrawerOpen: boolean
  setIsDrawerOpen: (boolean) => void
  lastExpandedIds: string[]
}

const generatePostTestUrl = (
  modulId: string,
  modulName: string,
  topikId: string,
  topikName: string,
  postTestState: PostTestState,
  from: string = '',
  menuId: string = ''
): string => {
  let baseUrl = `/pelatihan-mandiri/modul/${modulId}/post-test`

  if (postTestState === 'passed') {
    baseUrl = `${baseUrl}/passed`
  }

  return `${baseUrl}?${stringify({
    topik: topikId,
    topik_name: topikName,
    module_name: modulName,
    postTest: true,
    from: from,
    menuId: menuId,
    testPassed: postTestState === 'passed',
  })}`
}

const ModuleDetailsMobile = ({
  modulePreview,
  isLoading,
  isSuccess,
  materialData,
  toggleShowDetail,
  toggleMateriExpand,
  isDrawerOpen,
  lastExpandedIds,
  setIsDrawerOpen = () => {},
}: ModuleDetailsMobileProps) => {
  const router = useRouter()
  const { query, replace } = router
  const containerRef = React.useRef(null)

  const isDaljabModule = modulePreview?.data?.moduleType === 'DALJAB'

  useEffect(() => {
    if (isSuccess && isDaljabModule) {
      replace('/pelatihan-mandiri/404')
    }
  }, [isSuccess, isDaljabModule, replace])

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Topik {query.topik_name ?? ''}</title>
      </Head>
      <div
        ref={containerRef}
        className="bg-gray-5 "
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        {isLoading && isDaljabModule && <ModuleDetailsPlaceholder />}

        {isSuccess && !isDaljabModule && (
          <>
            <ModulePageHeading
              name={modulePreview.data.name}
              reviewer={{
                name: modulePreview.data.reviewer?.name,
                imageUrl: modulePreview.data.reviewer?.imageUrl,
              }}
              onToggleClick={toggleShowDetail}
            />
            <div data-testid="success-fetch" className="relative z-0">
              <div className="p-4 pb-6">
                <MaterialList
                  materialList={materialData?.data}
                  query={query}
                  toggleMateriExpand={toggleMateriExpand}
                  lastExpandedIds={lastExpandedIds}
                />

                <>
                  <Text className="mb-2 mt-6" variant="heading-sm">
                    Post Test
                  </Text>
                  <div className="mb-4">
                    <PostTestCard
                      href={generatePostTestUrl(
                        String(query.modul),
                        String(modulePreview.data.name),
                        String(query.topik),
                        String(query.topik_name),
                        modulePreview.data.postTest?.state
                      )}
                      title="Kerjakan post test"
                      description={
                        modulePreview.data.postTest?.state === 'not_passed'
                          ? 'Post test perlu diulang'
                          : 'Evaluasi pemahaman modul'
                      }
                      isLocked={!modulePreview.data.learningMaterial?.completed}
                      icon={
                        <CheckmarkIcon
                          postTestState={modulePreview.data.postTest?.state}
                        />
                      }
                      trackStep={() =>
                        GATrackEvent({
                          eventName: 'learning_activity',
                          eventParams: {
                            event_trigger: 'click_module_activity_card',
                            action_type: 'click_module_activity',
                            meta: JSON.stringify({
                              topik_id: String(query.topik),
                              module_id: String(query.modul),
                              type: 'post_test',
                            }),
                          },
                          sendDefaultParams: true,
                        })
                      }
                    />
                  </div>
                  {modulePreview.data.postTest?.state === 'not_passed' && (
                    <div className="mb-4">
                      <PostTestCard
                        href={`/${
                          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                          'pelatihan-mandiri'
                        }/modul/${query.modul}/post-test-history?${stringify({
                          topik: query.topik,
                          topik_name: query.topik_name,
                        })}`}
                        title="Hasil post test terakhir"
                        description={dayjs().format('DD/MM/YYYY')}
                        isLocked={
                          !modulePreview.data.learningMaterial?.completed
                        }
                        icon={<Icon>history</Icon>}
                        trackStep={() =>
                          GATrackEvent({
                            eventName: 'learning_activity',
                            eventParams: {
                              event_trigger: 'click_module_activity_card',
                              action_type: 'click_module_activity',
                              meta: JSON.stringify({
                                topik_id: String(query.topik),
                                module_id: String(query.modul),
                                type: 'post_test',
                              }),
                            },
                            sendDefaultParams: true,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              </div>
            </div>
            <ModuleDrawer
              isOpen={isDrawerOpen}
              onSheetClosed={() => {
                setIsDrawerOpen(false)
              }}
              modul={{
                name: modulePreview.data.name,
                description: modulePreview.data.description,
              }}
              reviewer={{
                name: modulePreview.data.reviewer?.name,
                imageUrl: modulePreview.data.reviewer?.imageUrl,
                about: modulePreview.data.reviewer?.about,
              }}
              materials={modulePreview.data.learningMaterial?.materials.map(
                (materi) => ({ id: materi.id, name: materi.name })
              )}
            />
          </>
        )}
      </div>
    </>
  )
}

export default ModuleDetailsMobile
